import { create } from 'zustand';

export const useAppStore = create((set, get) => ({
  // ─── Navigation ────────────────────────────────
  activeView: 'overview',
  setActiveView: (v) => set({ activeView: v }),
  isSidebarOpen: typeof window !== 'undefined' && window.innerWidth >= 768,
  setSidebarOpen: (v) => set({ isSidebarOpen: v }),
  toggleSidebar: () => set((s) => ({ isSidebarOpen: !s.isSidebarOpen })),

  // ─── Settings / Customise Tabs ─────────────────
  settingsTab: 'colors',
  setSettingsTab: (t) => set({ settingsTab: t }),
  customiseTab: 'context',
  setCustomiseTab: (t) => set({ customiseTab: t }),

  // ─── System Health ─────────────────────────────
  systemStatus: { overall: 'online', mode: 'local' },
  setSystemStatus: (s) => set({ systemStatus: s }),

  // ─── Auth ──────────────────────────────────────
  // sessionStorage keeps login alive within the tab session; Convex persists across sessions.
  isLoggedIn: typeof window !== 'undefined' && sessionStorage.getItem('palinos_session') === '1',
  logoutCount: 0,
  login: (email, password) => {
    if (email === 'gnanies@amazon.com' && password === 'Student@123@#$') {
      sessionStorage.setItem('palinos_session', '1');
      set({ isLoggedIn: true, logoutCount: 0 });
      return true;
    }
    return false;
  },
  logout: () => {
    sessionStorage.removeItem('palinos_session');
    set({ isLoggedIn: false, logoutCount: 0 });
  },
  incrementLogout: () => set((s) => ({ logoutCount: s.logoutCount + 1 })),

  // ─── User ──────────────────────────────────────
  userId: 'user_default',
  userName: 'Gnanesh',
  userEmail: 'gnanies@amazon.com',
  setUser: (id, name) => set({ userId: id, userName: name }),

  // ─── Current Model ─────────────────────────────
  currentModel: 'claude-sonnet-4-6',
  availableModels: [],
  setCurrentModel: (m) => set({ currentModel: m }),
  setAvailableModels: (m) => set({ availableModels: m }),

  // ─── Chat ──────────────────────────────────────
  conversations: [],
  conversationMessages: {},
  activeConversationId: null,
  messages: [],
  isStreaming: false,
  setConversations: (c) => set({ conversations: c }),
  createConversation: ({ title, model, userId, chatMode = 'api' }) => {
    const id = `conv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const now = Date.now();
    const convo = {
      id,
      title: (title || 'New Conversation').trim(),
      model,
      userId,
      chatMode,
      createdAt: now,
      updatedAt: now,
    };
    set((s) => ({
      conversations: [convo, ...s.conversations],
      activeConversationId: id,
      messages: [],
      conversationMessages: { ...s.conversationMessages, [id]: [] },
    }));
    return id;
  },
  setActiveConversation: (id) => set((s) => {
    const conv = s.conversations.find((c) => (c._id ?? c.id) === id);
    return {
      activeConversationId: id,
      messages: id ? (s.conversationMessages[id] || []) : [],
      ...(conv?.chatMode ? { chatMode: conv.chatMode } : {}),
    };
  }),
  setMessages: (m) => set({ messages: m }),
  addMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
  addConversationMessage: (conversationId, msg) => set((s) => {
    const existing = s.conversationMessages[conversationId] || [];
    const updatedMessages = [...existing, msg];
    const updatedConversations = s.conversations.map((c) => {
      const cId = c._id ?? c.id;
      return cId === conversationId
        ? {
            ...c,
            updatedAt: msg.createdAt || Date.now(),
            title: c.title || (msg.content || '').slice(0, 30) || 'New Conversation',
          }
        : c;
    });

    return {
      conversations: updatedConversations,
      conversationMessages: {
        ...s.conversationMessages,
        [conversationId]: updatedMessages,
      },
      messages: s.activeConversationId === conversationId ? updatedMessages : s.messages,
    };
  }),
  renameConversation: (id, title) => set((s) => ({
    conversations: s.conversations.map((c) => {
      const cId = c._id ?? c.id;
      return cId === id ? { ...c, title } : c;
    }),
  })),
  removeConversation: (id) => set((s) => {
    const newConvMessages = { ...s.conversationMessages };
    delete newConvMessages[id];
    return {
      conversations: s.conversations.filter((c) => (c._id ?? c.id) !== id),
      conversationMessages: newConvMessages,
      activeConversationId: s.activeConversationId === id ? null : s.activeConversationId,
      messages: s.activeConversationId === id ? [] : s.messages,
    };
  }),
  loadConversationMessages: (conversationId, msgs) => set((s) => ({
    conversationMessages: { ...s.conversationMessages, [conversationId]: msgs },
    messages: s.activeConversationId === conversationId ? msgs : s.messages,
  })),
  setStreaming: (v) => set({ isStreaming: v }),

  // ─── Memory ────────────────────────────────────
  // Source of truth is Convex when enabled; pure in-memory otherwise.
  memories: [],
  setMemories: (m) => set({ memories: m }),
  addMemory: (m) => set((s) => ({ memories: [...s.memories, m] })),

  // ─── Chat Mode ─────────────────────────────────
  // Defaults used until Convex preferences load; cloud values win once fetched.
  chatMode: 'cloudcall',   // 'api' | 'webgpu' | 'cloudcall'
  setChatMode: (m) => set({ chatMode: m }),
  webgpuModelId: 'onnx-community/gemma-4-E2B-it-ONNX',
  setWebgpuModelId: (id) => set({ webgpuModelId: id }),
  cloudcallModelId: '@cf/meta/llama-3.2-3b-instruct',
  setCloudcallModelId: (id) => set({ cloudcallModelId: id }),

  // ─── System Prompt Override ────────────────────
  // Stored in Convex users.preferences — global across all devices, all chats.
  // null = use built-in mode default. Only changes when user explicitly saves.
  customSystemPrompt: null,
  setCustomSystemPrompt: (p) => set({ customSystemPrompt: p }),

  // ─── Migrate local conv_... id → real Convex id ───────────────────
  // Called after Convex creates the conversation so all messages
  // (already sent with the local id) are ported to the cloud id.
  migrateConversationId: (localId, convexId) => set((s) => {
    const localMsgs = s.conversationMessages[localId] || [];
    const updatedConvMessages = { ...s.conversationMessages, [convexId]: localMsgs };
    delete updatedConvMessages[localId];
    const updatedConversations = s.conversations
      .map((c) => (c.id === localId || c._id === localId) ? { ...c, id: convexId, _id: convexId } : c)
      .filter((c, i, arr) => i === arr.findIndex((x) => (x._id ?? x.id) === (c._id ?? c.id)));
    return {
      conversations: updatedConversations,
      conversationMessages: updatedConvMessages,
      activeConversationId: s.activeConversationId === localId ? convexId : s.activeConversationId,
      messages: s.activeConversationId === localId ? localMsgs : s.messages,
    };
  }),

  // ─── Calendar ──────────────────────────────────
  calendarEvents: [],
  selectedDate: null,
  setCalendarEvents: (events) => set({ calendarEvents: events }),
  setSelectedDate: (d) => set({ selectedDate: d }),
  addCalendarEvent: (e) => set((s) => ({
    calendarEvents: [...s.calendarEvents, e],
    selectedDate: new Date(e.startTime),
    activeView: 'calendar',
  })),
  removeCalendarEvent: (id) => set((s) => ({
    calendarEvents: s.calendarEvents.filter((e) => (e.id ?? e._id) !== id),
  })),
  removeCalendarEventByTitle: (title) => set((s) => ({
    calendarEvents: s.calendarEvents.filter((e) => e.title?.toLowerCase() !== title?.toLowerCase()),
  })),
  updateCalendarEvent: (id, updates) => set((s) => ({
    calendarEvents: s.calendarEvents.map((e) => (e.id ?? e._id) === id ? { ...e, ...updates } : e),
  })),
  updateCalendarEventByTitle: (title, updates) => set((s) => ({
    calendarEvents: s.calendarEvents.map((e) =>
      e.title?.toLowerCase() === title?.toLowerCase() ? { ...e, ...updates } : e
    ),
  })),
  moveCalendarEventsByDate: (fromDateStr, toDateStr) => set((s) => {
    const fromD = new Date(fromDateStr + 'T00:00:00+05:30');
    const toD   = new Date(toDateStr   + 'T00:00:00+05:30');
    return {
      calendarEvents: s.calendarEvents.map((e) => {
        const evD = new Date(e.startTime);
        const sameDay = evD.getFullYear() === fromD.getFullYear() &&
                        evD.getMonth()    === fromD.getMonth()    &&
                        evD.getDate()     === fromD.getDate();
        if (!sameDay) return e;
        const moved = new Date(e.startTime);
        moved.setFullYear(toD.getFullYear(), toD.getMonth(), toD.getDate());
        return { ...e, startTime: moved.getTime() };
      }),
      selectedDate: toD,
      activeView: 'calendar',
    };
  }),

  // ─── Context Files ─────────────────────────────
  contextFiles: [],
  setContextFiles: (f) => set({ contextFiles: f }),

  // ─── API Keys ──────────────────────────────────
  apiKeys: [],
  setApiKeys: (k) => set({ apiKeys: k }),

  // ─── Artifacts ─────────────────────────────────
  artifacts: [],
  setArtifacts: (a) => set({ artifacts: a }),

  // ─── Folders ───────────────────────────────────
  folders: [],
  setFolders: (f) => set({ folders: f }),

  // ─── Clipboard toast ───────────────────────────
  copiedId: null,
  setCopiedId: (id) => {
    set({ copiedId: id });
    setTimeout(() => set({ copiedId: null }), 2000);
  },

  // ─── Fallback API notification ──────────────────
  fallbackNotification: null,
  showFallbackNotification: (provider) => {
    set({ fallbackNotification: { provider, at: Date.now() } });
    setTimeout(() => set({ fallbackNotification: null }), 5000);
  },
  clearFallbackNotification: () => set({ fallbackNotification: null }),

  // ─── Connectors ────────────────────────────────
  connectors: [],
  setConnectors: (c) => set({ connectors: c }),

  // ─── Tools ────────────────────────────────────
  tools: [],
  setTools: (t) => set({ tools: t }),

  // ─── Modals ────────────────────────────────────
  contextModal: { isOpen: false, mode: null, data: null },
  setContextModal: (m) => set({ contextModal: m }),
  closeContextModal: () => set({ contextModal: { isOpen: false, mode: null, data: null } }),

  // ─── Memory modal ──────────────────────────────
  memoryModal: { isOpen: false, memory: null },
  openMemoryModal: (memory) => set({ memoryModal: { isOpen: true, memory } }),
  closeMemoryModal: () => set({ memoryModal: { isOpen: false, memory: null } }),
}));
